import { useCallback, useRef, useState } from 'react';
import { extractList } from '../utils/response';

/**
 * Loads select/autocomplete options only when `load()` is called
 * (typically Autocomplete `onOpen` or Select `onOpen`).
 * Subsequent opens reuse the cached options unless `force` is true.
 *
 * @param {Function} lazyTrigger - RTK Query lazy trigger, e.g. from useLazyGetUsersListQuery()
 * @param {Object} [config]
 * @param {*} [config.args] - query args passed to the trigger
 * @param {(res: any) => any[]} [config.mapResult] - map API response → options array
 * @param {boolean} [config.enabled=true]
 */
export function useLazySelectOptions(lazyTrigger, config = {}) {
  const { args, mapResult, enabled = true } = config;
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const loadedRef = useRef(false);
  const inFlightRef = useRef(null);
  const optionsRef = useRef(options);
  const argsRef = useRef(args);
  const mapRef = useRef(mapResult);

  optionsRef.current = options;
  argsRef.current = args;
  mapRef.current = mapResult;

  const load = useCallback(
    async (force = false) => {
      if (!enabled) return [];
      if (!force && loadedRef.current) return optionsRef.current;
      if (!force && inFlightRef.current) return inFlightRef.current;

      const run = (async () => {
        setLoading(true);
        try {
          const res = await lazyTrigger(argsRef.current).unwrap();
          const mapper = mapRef.current || ((r) => extractList(r));
          const list = mapper(res);
          const next = Array.isArray(list) ? list : [];
          setOptions(next);
          loadedRef.current = true;
          return next;
        } catch (err) {
          setOptions([]);
          throw err;
        } finally {
          setLoading(false);
          inFlightRef.current = null;
        }
      })();

      inFlightRef.current = run;
      return run;
    },
    [enabled, lazyTrigger]
  );

  const reset = useCallback(() => {
    loadedRef.current = false;
    inFlightRef.current = null;
    setOptions([]);
    setLoading(false);
  }, []);

  return { options, loading, load, reset, setOptions };
}

export default useLazySelectOptions;
